<template>
  <div class="super-powers-page">
    <main>
      <section
        class="super-powers-section super-powers-section--dark super-powers-hero"
        aria-labelledby="super-powers-hero-title"
      >
        <v-container class="super-powers-container super-powers-split">
          <div class="super-powers-hero__content">
            <p class="super-powers-eyebrow">Engineering Capabilities</p>
            <h1 id="super-powers-hero-title" class="super-powers-title">
              Our Super Powers
            </h1>
            <p class="super-powers-subheading">
              Complex missions need more than a single specialty.
            </p>
            <div class="super-powers-copy super-powers-copy--lead">
              <p>
                Reckon assembles the right engineering disciplines around each challenge. Together,
                we turn unclear requirements and technical complexity into secure, dependable
                solutions that support the mission today and adapt for what comes next.
              </p>
            </div>
            <div class="super-powers-actions">
              <v-btn large color="red" href="#super-powers">Explore Capabilities</v-btn>
              <v-btn large outlined :to="{ name: 'reckon-way-people' }">Meet the Heroes</v-btn>
            </div>
          </div>

          <figure class="super-powers-hero__artwork">
            <img
              :src="engineeringHeroesImage | mediaSrc"
              alt="The Reckon engineering team collaborating around a mission success whiteboard"
            >
          </figure>
        </v-container>
      </section>

      <section
        class="super-powers-section super-powers-section--charcoal"
        aria-labelledby="solve-problems-title"
      >
        <v-container class="super-powers-container super-powers-split super-powers-split--copy">
          <div>
            <h2 id="solve-problems-title" class="super-powers-title">How We Solve Problems</h2>
            <div class="super-powers-copy super-powers-copy--lead">
              <p class="super-powers-statement">Start with the mission. Design for change.</p>
              <p>
                Before choosing tools or writing code, we define the problem, the people it affects,
                and the outcome that matters. That shared understanding guides every design and
                delivery decision.
              </p>
              <p>
                We then work in a continuous loop: design, build, test, deliver, learn from real use,
                and improve. Progress stays measurable, risks surface earlier, and the solution can
                evolve without losing sight of its purpose.
              </p>
            </div>
          </div>

          <figure class="problem-path">
            <figcaption class="problem-path__caption">
              <span>Continuous Engineering Loop</span>
              <strong>Every phase strengthens the next.</strong>
            </figcaption>

            <ol class="problem-path__steps">
              <li
                v-for="(step, index) in engineeringProcessSteps"
                :key="step.name"
              >
                <span class="problem-path__number">{{ String(index + 1).padStart(2, '0') }}</span>
                <span class="problem-path__step-copy">
                  <strong>{{ step.name }}</strong>
                  <small>{{ step.purpose }}</small>
                </span>
                <span class="problem-path__arrow" aria-hidden="true">{{ step.arrow }}</span>
              </li>
            </ol>

            <div class="problem-path__outcome">
              <span>Shared Result</span>
              <strong>Better Mission Outcomes</strong>
              <small>Useful. Reliable. Ready to evolve.</small>
            </div>
          </figure>
        </v-container>
      </section>

      <section
        id="super-powers"
        class="super-powers-section super-powers-section--dark"
        aria-labelledby="super-powers-title"
      >
        <v-container class="super-powers-container">
          <div class="super-powers-intro">
            <p class="super-powers-eyebrow">Seven Disciplines</p>
            <h2 id="super-powers-title" class="super-powers-title">
              Expertise Across the Mission
            </h2>
          </div>

          <div class="super-powers-grid">
            <router-link
              v-for="power in superPowers"
              :key="power.slug"
              :to="{ name: 'capability-detail', params: { capabilitySlug: power.slug } }"
              class="super-power-card"
              :aria-label="`Explore ${power.title}`"
            >
              <div class="super-power-card__content">
                <h3 class="super-power-card__title">{{ power.title }}</h3>
                <p class="super-power-card__hero-label hero-identity-label">
                  {{ power.heroLabel }}
                </p>
                <p class="super-power-card__description">{{ power.description }}</p>
                <span class="super-power-card__cta">
                  Explore <v-icon small>mdi-arrow-right</v-icon>
                </span>
              </div>
            </router-link>
          </div>
        </v-container>
      </section>

      <section
        class="super-powers-section super-powers-section--charcoal"
        aria-labelledby="people-behind-powers-title"
      >
        <v-container class="super-powers-container super-powers-split super-powers-split--people">
          <figure class="power-network">
            <figcaption class="power-network__caption">
              <span>One Crew</span>
              <strong>Different specialties. Shared mission.</strong>
            </figcaption>

            <div class="power-network__roles">
              <div
                v-for="(power, index) in superPowers"
                :key="power.heroLabel"
                class="power-network__role"
              >
                <span>{{ String(index + 1).padStart(2, '0') }}</span>
                <div>
                  <strong>{{ power.heroLabel }}</strong>
                  <small>{{ power.title }}</small>
                </div>
              </div>
            </div>

            <div class="power-network__merge" aria-hidden="true">
              <span />
              <strong>Shared ownership + continuous collaboration</strong>
              <span />
            </div>

            <div class="power-network__core">
              <span>Reckon</span>
              <strong>One Multidisciplinary Crew</strong>
              <small>Communicate better. Adapt faster. Solve harder problems.</small>
            </div>
          </figure>

          <div>
            <p class="super-powers-eyebrow">Why Reckon</p>
            <h2 id="people-behind-powers-title" class="super-powers-title">
              Built as One Crew
            </h2>
            <div class="super-powers-copy super-powers-copy--lead">
              <p class="super-powers-statement">Specialists work better when ownership is shared.</p>
              <p>
                Our engineers do not hand work from one silo to the next. They plan together,
                challenge assumptions early, and stay accountable from discovery through operation.
              </p>
              <p>
                That operating model reduces friction, keeps decisions connected to the mission,
                and gives customers one team focused on the complete outcome.
              </p>
            </div>
          </div>
        </v-container>
      </section>

      <section
        class="super-powers-section super-powers-section--cta cta-section"
        aria-labelledby="super-powers-cta-title"
      >
        <v-container class="super-powers-container super-powers-cta">
          <div>
            <h2 id="super-powers-cta-title" class="super-powers-title">
              Ready to Put Our Powers to Work?
            </h2>
            <div class="super-powers-copy super-powers-copy--lead">
              <p class="super-powers-statement super-powers-statement--light">
                Have a problem worth solving?
              </p>
              <p>
                Tell us the outcome you need. We’ll help identify the right path and the right crew.
              </p>
            </div>
          </div>
          <div class="super-powers-actions super-powers-actions--end">
            <v-btn large color="red" :to="{ name: 'contact' }">Send the Signal</v-btn>
            <v-btn large outlined :to="{ name: 'reckon-way-people' }">Meet the Heroes</v-btn>
          </div>
        </v-container>
      </section>
    </main>

    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '@/components/Footer.vue';
import { mediaRef } from '@/services/mediaAssets';
import { capabilitiesBySlug } from '@/content/capabilities';

const superPowerDefinitions = [
  {
    slug: 'software-engineering',
    description: 'Turning complex ideas into reliable software designed to evolve with the mission.',
  },
  {
    slug: 'systems-engineering',
    description: 'Seeing how every piece fits together and turning complexity into systems that work as one.',
  },
  {
    slug: 'cloud-solutions',
    description: 'Designing secure, resilient environments built to scale when they’re needed most.',
  },
  {
    slug: 'devsecops',
    description: 'Breaking down barriers between development, security, and operations to move great software forward.',
  },
  {
    slug: 'quality-engineering',
    description: 'Hunting down defects, challenging assumptions, and making sure software is ready when it matters.',
  },
  {
    slug: 'program-delivery',
    description: 'Keeping people, priorities, and complex technical efforts moving toward the same goal.',
  },
  {
    slug: 'data-ai',
    description: 'Finding patterns in complexity and turning data into insight people can use.',
  },
];

const superPowers = superPowerDefinitions.map((power) => {
  const capability = capabilitiesBySlug[power.slug];

  return {
    ...power,
    title: capability.title,
    heroLabel: capability.heroLabel,
  };
});

const engineeringProcessSteps = [
  { name: 'Understand', purpose: 'Define success', arrow: '→' },
  { name: 'Design', purpose: 'Shape the approach', arrow: '↓' },
  { name: 'Build', purpose: 'Create with care', arrow: '←' },
  { name: 'Test', purpose: 'Prove it works', arrow: '↓' },
  { name: 'Deliver', purpose: 'Put value to work', arrow: '→' },
  { name: 'Improve', purpose: 'Learn and evolve', arrow: '↻' },
];

export default {
  name: 'CapabilitiesView',
  components: {
    AppFooter,
  },
  data: () => ({
    engineeringProcessSteps,
    engineeringHeroesImage: mediaRef('capabilities/engineering-heroes.png'),
    superPowers,
  }),
};
</script>

<style scoped>
.super-powers-page {
  background: #080808;
  color: #fff;
}

.super-powers-section {
  padding: 96px 0;
  scroll-margin-top: 72px;
}

.super-powers-section--dark {
  background: #080808;
}

.super-powers-section--charcoal {
  background:
    radial-gradient(circle at 88% 14%, rgba(226, 50, 50, .09), transparent 34%),
    #111;
  border-bottom: 1px solid #2c2c2c;
  border-top: 1px solid #2c2c2c;
}

.super-powers-section--cta {
  background: #0d0d0d;
  border-top: 1px solid #333;
}

.super-powers-container {
  max-width: 1180px;
}

.super-powers-hero {
  padding-top: 72px;
}

.super-powers-split,
.super-powers-cta {
  align-items: center;
  display: grid;
  gap: clamp(44px, 7vw, 90px);
  grid-template-columns: minmax(0, 1.02fr) minmax(340px, .98fr);
}

.super-powers-split--copy {
  grid-template-columns: minmax(0, 1.2fr) minmax(340px, .8fr);
}

.super-powers-split--people {
  grid-template-columns: minmax(360px, .85fr) minmax(0, 1.15fr);
}

.super-powers-hero__content {
  padding: 42px 0;
}

.super-powers-hero__artwork {
  background: #111;
  border: 1px solid #383838;
  margin: 0;
  overflow: hidden;
}

.super-powers-hero__artwork img {
  display: block;
  height: auto;
  width: 100%;
}

.super-powers-eyebrow,
.super-power-card__hero-label {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .82rem;
  font-weight: 900;
  letter-spacing: .1em;
  margin: 0 0 12px;
  text-transform: uppercase;
}

.super-powers-title {
  font-family: "Oswald", sans-serif;
  font-size: clamp(2.6rem, 5vw, 4.7rem);
  line-height: 1.03;
  margin: 0 0 28px;
}

.super-powers-subheading {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: clamp(1.45rem, 2.5vw, 2rem);
  line-height: 1.25;
  margin: -8px 0 24px;
}

.super-powers-copy {
  max-width: 760px;
}

.super-powers-copy p {
  color: #bcbcbc;
  font-size: 1.08rem;
  line-height: 1.75;
}

.super-powers-copy--lead p {
  font-size: 1.15rem;
}

.super-powers-section--charcoal .super-powers-copy p {
  color: #bdbdbd;
}

.super-powers-copy p:first-child {
  margin-top: 0;
}

.super-powers-copy p:last-child {
  margin-bottom: 0;
}

.super-powers-statement {
  color: #fff !important;
  font-family: "Oswald", sans-serif;
  font-size: clamp(1.8rem, 3vw, 2.65rem) !important;
  font-weight: 800;
  line-height: 1.2 !important;
}

.super-powers-statement--light {
  color: #fff !important;
}

.super-powers-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
}

.super-powers-actions--end {
  justify-content: flex-end;
}

.problem-path {
  background: linear-gradient(145deg, #191919, #0d0d0d);
  border: 1px solid #393939;
  box-shadow: 0 24px 70px rgba(0, 0, 0, .28);
  margin: 0;
  overflow: hidden;
  padding: 28px;
  position: relative;
}

.problem-path::after {
  background: #e23232;
  content: "";
  height: 3px;
  left: 28px;
  position: absolute;
  right: 28px;
  top: 0;
}

.problem-path__caption,
.power-network__caption {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 22px;
}

.problem-path__caption span,
.power-network__caption span,
.problem-path__outcome span,
.power-network__core span {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .7rem;
  font-weight: 900;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.problem-path__caption strong,
.power-network__caption strong {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1.2rem;
  line-height: 1.2;
}

.problem-path__steps {
  display: grid;
  gap: 8px 28px;
  grid-template-areas:
    "one two"
    "four three"
    "five six";
  grid-template-columns: repeat(2, minmax(0, 1fr));
  list-style: none;
  margin: 0;
  padding: 0;
}

.problem-path__steps li {
  align-items: center;
  background: #202020;
  border: 1px solid #333;
  display: grid;
  gap: 10px;
  grid-template-columns: auto 1fr auto;
  min-height: 68px;
  padding: 10px 12px;
  position: relative;
}

.problem-path__steps li:nth-child(1) { grid-area: one; }
.problem-path__steps li:nth-child(2) { grid-area: two; }
.problem-path__steps li:nth-child(3) { grid-area: three; }
.problem-path__steps li:nth-child(4) { grid-area: four; }
.problem-path__steps li:nth-child(5) { grid-area: five; }
.problem-path__steps li:nth-child(6) { grid-area: six; }

.problem-path__number {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .72rem;
  font-weight: 900;
}

.problem-path__step-copy {
  display: flex;
  flex-direction: column;
}

.problem-path__step-copy strong {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: .95rem;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.problem-path__step-copy small {
  color: #979797;
  font-size: .68rem;
  line-height: 1.25;
}

.problem-path__arrow {
  color: #e23232;
  font-size: 1.05rem;
  font-weight: 900;
}

.problem-path__outcome {
  align-items: center;
  background: linear-gradient(110deg, rgba(226, 50, 50, .2), rgba(226, 50, 50, .05));
  border: 1px solid rgba(226, 50, 50, .55);
  display: flex;
  flex-direction: column;
  margin-top: 18px;
  padding: 18px;
  text-align: center;
}

.problem-path__outcome strong {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1.45rem;
  line-height: 1.2;
  text-transform: uppercase;
}

.problem-path__outcome small {
  color: #bdbdbd;
  font-size: .75rem;
  margin-top: 3px;
}

.super-powers-intro {
  margin-bottom: 42px;
}

.super-powers-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.super-power-card {
  background: #141414;
  border: 1px solid #303030;
  color: inherit;
  display: flex;
  flex-direction: column;
  min-height: 260px;
  text-decoration: none;
  transition: border-color 180ms ease, transform 180ms ease;
}

.super-power-card:hover,
.super-power-card:focus-visible {
  border-color: #e23232;
  outline: none;
  transform: translateY(-4px);
}

.super-power-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 32px 30px;
}

.super-power-card__title {
  font-family: "Oswald", sans-serif;
  font-size: 1.8rem;
  line-height: 1.16;
  margin: 0 0 14px;
}

.super-power-card__hero-label {
  margin-bottom: 24px;
}

.super-power-card__description {
  color: #bdbdbd;
  font-size: 1.02rem;
  line-height: 1.65;
  margin: 0;
}

.super-power-card__cta {
  align-items: center;
  color: #fff;
  display: inline-flex;
  font-family: "Oswald", sans-serif;
  font-size: .82rem;
  font-weight: 900;
  gap: 5px;
  margin-top: auto;
  padding-top: 24px;
  text-transform: uppercase;
}

.super-power-card__cta .v-icon { color: #e23232; }
.super-power-card:hover .super-power-card__cta { color: #e23232; }

.power-network {
  background: linear-gradient(145deg, #191919, #0d0d0d);
  border: 1px solid #393939;
  box-shadow: 0 24px 70px rgba(0, 0, 0, .28);
  margin: 0;
  max-width: 540px;
  overflow: hidden;
  padding: 28px;
  position: relative;
  width: 100%;
}

.power-network::before {
  background: #e23232;
  content: "";
  height: 3px;
  left: 28px;
  position: absolute;
  right: 28px;
  top: 0;
}

.power-network__roles {
  display: grid;
  gap: 7px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.power-network__role {
  align-items: center;
  background: #202020;
  border: 1px solid #333;
  border-left: 3px solid #e23232;
  display: grid;
  gap: 10px;
  grid-template-columns: auto 1fr;
  min-height: 57px;
  padding: 8px 10px;
}

.power-network__role > span {
  color: #666;
  font-family: "Oswald", sans-serif;
  font-size: .7rem;
  font-weight: 900;
}

.power-network__role div {
  display: flex;
  flex-direction: column;
}

.power-network__role strong {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: .78rem;
  line-height: 1.2;
  text-transform: uppercase;
}

.power-network__role small {
  color: #8f8f8f;
  font-size: .62rem;
  line-height: 1.2;
}

.power-network__merge {
  align-items: center;
  color: #8f8f8f;
  display: grid;
  font-size: .63rem;
  gap: 10px;
  grid-template-columns: 1fr auto 1fr;
  letter-spacing: .05em;
  margin: 16px 0;
  text-align: center;
  text-transform: uppercase;
}

.power-network__merge span {
  background: linear-gradient(90deg, transparent, #e23232);
  height: 1px;
}

.power-network__merge span:last-child {
  background: linear-gradient(90deg, #e23232, transparent);
}

.power-network__core {
  align-items: center;
  background: linear-gradient(110deg, rgba(226, 50, 50, .22), rgba(226, 50, 50, .05));
  border: 1px solid rgba(226, 50, 50, .6);
  display: flex;
  flex-direction: column;
  padding: 18px;
  text-align: center;
}

.power-network__core strong {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1.35rem;
  line-height: 1.2;
  text-transform: uppercase;
}

.power-network__core small {
  color: #bdbdbd;
  font-size: .72rem;
  margin-top: 4px;
}

.super-powers-cta {
  align-items: end;
}

@media (max-width: 900px) {
  .super-powers-split,
  .super-powers-split--copy,
  .super-powers-split--people,
  .super-powers-cta {
    grid-template-columns: 1fr;
  }

  .super-powers-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .super-powers-actions--end {
    justify-content: flex-start;
  }

  .power-network {
    margin: 0 auto;
  }
}

@media (max-width: 650px) {
  .super-powers-section {
    padding: 72px 0;
  }

  .super-powers-hero {
    padding-top: 30px;
  }

  .super-powers-hero__content {
    padding-bottom: 0;
  }

  .super-powers-grid {
    grid-template-columns: 1fr;
  }

  .super-power-card {
    min-height: 0;
  }

  .super-powers-actions .v-btn {
    width: 100%;
  }

  .problem-path {
    padding: 24px 18px 18px;
  }

  .problem-path::after,
  .power-network::before {
    left: 18px;
    right: 18px;
  }

  .problem-path__steps {
    gap: 7px;
    grid-template-areas: "one" "two" "three" "four" "five" "six";
    grid-template-columns: 1fr;
  }

  .problem-path__arrow {
    color: transparent;
    position: relative;
  }

  .problem-path__arrow::after {
    color: #e23232;
    content: "\2193";
    position: absolute;
    right: 0;
  }

  .power-network {
    padding: 24px 18px 18px;
  }

  .power-network__roles {
    grid-template-columns: 1fr;
  }

  .power-network__merge {
    grid-template-columns: 24px 1fr 24px;
  }
}
</style>
